import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Product } from "../components/Shopping/Prodcuts";
import {
  DeviceOption,
  deviceAddOnPrice,
  normalizeDeviceOption,
} from "../components/Shopping/deviceOptions";

/**
 * The single source of truth for the shopping cart.
 */

const CART_KEY = "mrehab_cart";

export interface CartItem {
  id?: string;
  product: Product;
  color: string;
  weight: string;
  quantity: number;
  device?: string;
  deviceOption?: DeviceOption;
  caseLink?: string;
}

/** A line is checkout-ready once we know which handset it runs on. */
export function isLineComplete(item: CartItem): boolean {
  return normalizeDeviceOption(item.deviceOption) === "addon" || !!item.device;
}

/** Kit plus any device add-on, for the whole line. */
export function lineTotal(item: CartItem): number {
  return (
    (item.product.price + deviceAddOnPrice(item.deviceOption)) * item.quantity
  );
}

export interface Toast {
  id: number;
  title: string;
  detail?: string;
  /** Renders a "View cart" link inside the toast. */
  cartLink?: boolean;
}

type CartContextValue = {
  items: CartItem[];
  /** Sum of quantities — the badge number. */
  count: number;
  subtotal: number;
  /** Items still missing a phone model. addon-device lines never count. */
  incompleteCount: number;
  addItem: (item: CartItem) => void;
  updateItem: (index: number, updates: Partial<CartItem>) => void;
  removeItem: (index: number) => void;
  /** `qty <= 0` removes the line. */
  setQuantity: (index: number, qty: number) => void;
  clear: () => void;
  toasts: Toast[];
  showToast: (toast: Omit<Toast, "id">) => void;
  dismissToast: (id: number) => void;
};

let nextLineSuffix = 0;

/** A fresh line id. `randomUUID` is missing in older Safari and in jsdom. */
function newLineId(): string {
  const webCrypto = typeof crypto !== "undefined" ? crypto : undefined;
  if (typeof webCrypto?.randomUUID === "function") return webCrypto.randomUUID();
  return `line-${Date.now().toString(36)}-${(nextLineSuffix++).toString(36)}`;
}

/** Compare devices as text so `undefined` and `""` mean the same thing. */
function normalizeDevice(device?: string): string {
  return (device ?? "").trim();
}

/**
 * Parse a serialized cart
 */
function parseCart(raw: string | null, onBackfill?: () => void): CartItem[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item): item is CartItem =>
          !!item && typeof item === "object" && !!item.product
      )
      .map((item) => {
        // Normalize on read: a cart saved before the device add-on existed has
        // no `deviceOption` at all, and one saved before line ids no `id`.
        const normalized: CartItem = {
          ...item,
          deviceOption: normalizeDeviceOption(item.deviceOption),
        };
        if (normalized.id) return normalized;
        onBackfill?.();
        return { ...normalized, id: newLineId() };
      });
  } catch {
    return [];
  }
}

/** The one place the cart is written. Quota / private-window safe. */
function writeCart(next: CartItem[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CART_KEY, JSON.stringify(next));
  } catch {
    /* private window or quota exceeded — keep the in-memory cart working */
  }
}

/**
 * Hydrate from storage, writing back any ids that had to be backfilled.
 *
 * The write-back happens here rather than in an effect: child effects run
 * before the provider's own, so a descendant that clears the cart on mount
 * (BuyNowSuccess, on the Stripe return) would be undone by a later write of
 * the pre-clear snapshot.
 */
function readStoredCart(): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    let backfilled = false;
    const items = parseCart(window.localStorage.getItem(CART_KEY), () => {
      backfilled = true;
    });
    if (backfilled) writeCart(items);
    return items;
  } catch {
    return [];
  }
}

const CartContext = createContext<CartContextValue | null>(null);

let nextToastId = 1;

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(readStoredCart);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const commit = useCallback(
    (updater: (prev: CartItem[]) => CartItem[]) => {
      setItems((prev) => {
        const next = updater(prev);
        writeCart(next);
        return next;
      });
    },
    []
  );

  const addItem = useCallback(
    (incoming: CartItem) => {
      commit((prev) => {
        const incomingDevice = normalizeDevice(incoming.device);
        const incomingOption = normalizeDeviceOption(incoming.deviceOption);
        const sameConfiguration = (item: CartItem) =>
          item.product.id === incoming.product.id &&
          item.color === incoming.color &&
          item.weight === incoming.weight &&
          normalizeDeviceOption(item.deviceOption) === incomingOption;

        // 1st Pass - the same kit for the same phone. `caseLink` is deliberately
        // not part of the key.
        let index = prev.findIndex(
          (item) =>
            sameConfiguration(item) &&
            normalizeDevice(item.device) === incomingDevice
        );

        // 2nd Pass - repair a line saved before device collection existed.
        // Legacy lines normalize to `byod`, so an "Add Device" line can never
        // reach this pass and absorb one.
        if (index === -1 && incomingDevice) {
          index = prev.findIndex(
            (item) => sameConfiguration(item) && !normalizeDevice(item.device)
          );
        }

        if (index === -1) {
          return [...prev, { ...incoming, id: incoming.id ?? newLineId() }];
        }

        const next = [...prev];
        const existing = next[index];
        next[index] = {
          ...existing,
          quantity: existing.quantity + incoming.quantity,
          device: incoming.device ?? existing.device,
          deviceOption: incomingOption,
          // Not `??`: a addon line has no case link, and `??` would
          // resurrect the one already on the row.
          caseLink:
            incomingOption === "addon"
              ? undefined
              : incoming.caseLink ?? existing.caseLink,
        };
        return next;
      });
    },
    [commit]
  );

  const updateItem = useCallback(
    (index: number, updates: Partial<CartItem>) => {
      commit((prev) =>
        prev.map((item, i) => (i === index ? { ...item, ...updates } : item))
      );
    },
    [commit]
  );

  const removeItem = useCallback(
    (index: number) => {
      commit((prev) => prev.filter((_, i) => i !== index));
    },
    [commit]
  );

  const setQuantity = useCallback(
    (index: number, qty: number) => {
      if (qty <= 0) {
        removeItem(index);
        return;
      }
      updateItem(index, { quantity: qty });
    },
    [removeItem, updateItem]
  );

  const clear = useCallback(() => {
    commit(() => []);
  }, [commit]);

  const showToast = useCallback((toast: Omit<Toast, "id">) => {
    const id = nextToastId++;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Keep a second tab in sync. Harvested from the old Shopping/Main/TopBar.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== CART_KEY) return;
      setItems(parseCart(event.newValue));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const count = useMemo(
    () => items.reduce((sum, item) => sum + (item.quantity ?? 0), 0),
    [items]
  );

  const subtotal = useMemo(
    () =>
      items.reduce((sum, item) => sum + lineTotal(item), 0),
    [items]
  );

  const incompleteCount = useMemo(
    () => items.filter((item) => !isLineComplete(item)).length,
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      count,
      subtotal,
      incompleteCount,
      addItem,
      updateItem,
      removeItem,
      setQuantity,
      clear,
      toasts,
      showToast,
      dismissToast,
    }),
    [
      items,
      count,
      subtotal,
      incompleteCount,
      addItem,
      updateItem,
      removeItem,
      setQuantity,
      clear,
      toasts,
      showToast,
      dismissToast,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within CartProvider");
  }
  return ctx;
}
